import { addDays, addMonths, format, parse } from "date-fns"
import { formatDate } from "@/lib/format-date"

export const DateOnlyFormat = "yyyy-MM-dd"

export class DateOnly {
  private date: string

  constructor(date: Date | string) {
    if (date instanceof Date) {
      this.date = format(date, DateOnlyFormat)
    } else {
      const parsed = parse(date, DateOnlyFormat, new Date())

      // If parsing failed, Invalid Date will be returned. Invalid Date is a Date, whose time value is NaN.
      // See https://date-fns.org/v3.1.0/docs/parse
      if (Number.isNaN(parsed.getTime())) {
        throw `Parsing the date '${date}' as '${DateOnlyFormat}' failed`
      }
      this.date = date
    }
  }

  addDays(days: number): DateOnly {
    const newDate = addDays(this.toDate(), days)
    return new DateOnly(newDate)
  }

  addMonths(months: number): DateOnly {
    const newDate = addMonths(this.toDate(), months)
    return new DateOnly(newDate)
  }

  isGreaterOrEqualThan(min: DateOnly) {
    return this.toDate() >= min.toDate()
  }

  isGreaterThan(min: DateOnly) {
    return this.toDate() > min.toDate()
  }

  isLowerOrEqualThan(max: DateOnly) {
    return this.toDate() <= max.toDate()
  }

  isLowerThan(max: DateOnly) {
    return this.toDate() < max.toDate()
  }

  isWithinIncludingRange(min: DateOnly, max: DateOnly) {
    return this.isGreaterOrEqualThan(min) && this.isLowerOrEqualThan(max)
  }

  equals(dateOnly: DateOnly) {
    return this.date === dateOnly.date
  }

  notEquals(dateOnly: DateOnly) {
    return this.date !== dateOnly.date
  }

  toDate(): Date {
    return parse(this.date, DateOnlyFormat, new Date())
  }

  toString(): string {
    return this.date
  }

  toDisplayableString(): string {
    return formatDate(this.toDate())
  }
}
